import React from 'react';
import { Sparkles, Home, Square, SunMedium, Fence, Building2, HardHat, Sofa, BadgePercent, Droplets } from 'lucide-react';
import { useCatalog } from '../lib/useCatalog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

const iconMap = {
  Sparkles: Sparkles,
  Home: Home,
  Building2: Building2,
  HomeIcon: Home,
  HardHat: HardHat,
  Sofa: Sofa,
  Droplets: Droplets
};

const optionIcons = {
  encadrements: Square,
  volets: SunMedium,
  terrasse: Fence
};

const Services = () => {
  const { services, options } = useCatalog();
  // Group services by category
  const vitresServices = services.filter(s => s.category === 'vitres');
  const menageServices = services.filter(s => s.category === 'menage');
  const specialServices = services.filter(s => s.category === 'special');

  const ServiceCard = ({ service, index }) => {
    const IconComponent = iconMap[service.icon] || Sparkles;
    return (
      <Card
        key={service.id}
        className="group hover-lift border-0 shadow-lg hover:shadow-2xl bg-gradient-to-br from-white to-blue-50/30 overflow-hidden relative"
        style={{ animationDelay: `${index * 0.1}s` }}
      >
        {service.hasDeduction && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-green-500 hover:bg-green-600 text-white font-semibold flex items-center gap-1">
              <BadgePercent className="w-3 h-3" />
              -50% d'impôts
            </Badge>
          </div>
        )}
        <CardHeader className="pb-4">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[hsl(207,90%,54%)] to-[hsl(207,90%,44%)] flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
            <IconComponent className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-[hsl(207,90%,54%)] transition-colors">
            {service.name}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-gray-600 text-base leading-relaxed">
            {service.description}
          </CardDescription>
        </CardContent>
      </Card>
    );
  };

  return (
    <section id="services" className="section-padding bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-orange-50 rounded-full blur-3xl opacity-50 translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[hsl(32,95%,55%)] font-semibold text-sm uppercase tracking-wider mb-4">
            Nos prestations
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Services de nettoyage{' '}
            <span className="text-gradient">professionnels</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Découvrez notre gamme complète de services pour des espaces propres et lumineux
          </p>
        </div>

        {/* Vitres & Vérandas Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-[hsl(207,90%,54%)]" />
            Vitres & Vérandas
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {vitresServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>

        {/* Ménage Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
            <Home className="w-6 h-6 text-[hsl(207,90%,54%)]" />
            Ménage Professionnels & Particuliers
          </h3>
          <p className="text-gray-600 mb-8 max-w-3xl">
            Que vous soyez un professionnel (commerces, bureaux, restaurants) ou un particulier, 
            nous prenons soin de vos espaces. <a href="/credit-impot-50-particuliers" className="font-semibold text-green-600 hover:underline">Les particuliers bénéficient de 50 % de crédit d'impôt</a> sur les prestations de nettoyage et de ménage à domicile, grâce à notre adhésion à la coopérative Unipros.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {menageServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>

        {/* Services Spéciaux Section */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3">
            <HardHat className="w-6 h-6 text-[hsl(207,90%,54%)]" />
            Services Spécialisés
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {specialServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Options supplémentaires
            </h3>
            <p className="text-gray-600">Personnalisez votre prestation avec nos options</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {options.map((option, index) => {
              const IconComponent = optionIcons[option.id] || Square;
              return (
                <div
                  key={option.id}
                  className="group bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer border border-transparent hover:border-[hsl(32,95%,55%)]"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-4 group-hover:bg-[hsl(32,95%,55%)] transition-colors duration-300">
                    <IconComponent className="w-6 h-6 text-[hsl(32,95%,55%)] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2 group-hover:text-[hsl(207,90%,54%)] transition-colors">
                    {option.name}
                  </h4>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
